import React from "react";
import { DateTime } from "luxon";
import { DispatchProp } from "react-redux";
import { range, find, times } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { ChoiceListControl } from "./ChoiceListControl";
import {
    getSettingBoolean,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty, removeHtmlTags } from "../../utils";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { dateFromValue, dateFormat, size } from "../../models/utilities/questionUtilities";
import { translate } from "../../models/utilities/translationUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import {
    IMobiscroll,
    Calendar,
    MbscCalendarOptions,
} from "../../../lib/mobiscroll/js/mobiscroll.javascript.min";
import { cDateFormatToMobiscroll } from "../../utils/time";
import Select, { Option } from "../select/Select";
import { getChoice } from "../../models/utilities/variableUtilities";
import TextAnswerRenderer from "../renderers/v2/TextAnswerRenderer";
import { respondentSetAppointment } from "../../redux/actions";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

/* import Mobiscroll JS, Typings and CSS */
const mobiscroll: IMobiscroll = require("../../../lib/mobiscroll/js/mobiscroll.javascript.min.js");

type MbscSize = "xsmall" | "small" | "medium" | "large" | "xlarge" | number;
type MbscDisplay = "center" | "inline" | "bubble" | "top" | "bottom";
type MbscControl = "date" | "time" | "calendar";

const MOBISCROLL_THEME = "voxco";

const daysRange = [1, 31];
const hoursRange = [0, 23];
const minutesRange = [0, 59];
const weekDays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const initialState = {
    value: "",
    year: "",
    month: null,
    date: "",
    hour: "00",
    minute: "00",
};

interface Props extends ControlProps, DispatchProp {}

interface State {
    value: string;
    year: string;
    month: number;
    date: string;
    hour: string;
    minute: string;
}

export class DateTimeControl extends BaseControl<Props, State> {
    inputRef: HTMLElement;
    inputDivRef: HTMLElement;
    picker?: Calendar;

    constructor(props: Props) {
        super(props);
        this.state = { ...initialState };
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        this.setState({ ...initialState });
        this.updateState(props);
        this.bindDatePicker(props);
    }

    willUpdateMentions(question: QuestionModel, nextProps: Props) {
        super.willUpdateMentions(question, nextProps);
        this.updateState(nextProps);
    }

    finalizeQuestion(question: QuestionModel, props: Props) {
        super.finalizeQuestion(question, props);
        if (this.picker) {
            this.picker.destroy();
            this.picker = null;
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.question !== this.props.question ||
            prevProps.language !== this.props.language
        ) {
            this.bindDatePicker(this.props);
        }
    }

    componentDidMount() {
        this.bindDatePicker(this.props);
    }

    openDatePicker() {
        if (this.picker && !this.picker.isVisible()) {
            this.picker.show();
        }
    }

    isYearType = (question: QuestionModel) => getSettingBoolean(question, "Type", "Year");

    isMonthType = (question: QuestionModel) => getSettingBoolean(question, "Type", "Month");

    isDayType = (question: QuestionModel) => getSettingBoolean(question, "Type", "Day");

    isHourType = (question: QuestionModel) => getSettingBoolean(question, "Type", "Hours");

    isMinuteType = (question: QuestionModel) => getSettingBoolean(question, "Type", "Minutes");

    findAnswer = (props: Props, value: string) => getChoice(props.sequencedVariables[0], value);

    updateState(props: Props) {
        this.setState((prevState) => {
            const newState = { ...prevState };
            newState.value = undefined;
            const [mention] = this.getMentions(props);
            if (mention !== undefined) {
                newState.value = mention.value;
            }
            if (this.isMonthType(props.question)) {
                newState.month = Number(newState.value);
            }
            if (this.isDayType(props.question)) {
                newState.date = Number(newState.value).toString();
            }
            if (this.isHourType(props.question)) {
                newState.hour = newState.value;
            }
            if (this.isMinuteType(props.question)) {
                newState.minute = newState.value;
            }
            if (newState.value == null || this.findAnswer(this.props, newState.value) != null) {
                newState.value = "";
            }
            if (this.picker) {
                const value = this.dateValue();
                this.picker.setVal(value);
            }
            return newState;
        });
    }

    minDate = () => {
        const { min: value, question } = this.props;
        const isMinDateToday =
            value?.toString().split("T")[0] === new Date().toISOString().split("T")[0];

        if (isNullOrEmpty(value)) {
            return undefined;
        }
        if (value === "TodayValue" || isMinDateToday) {
            return DateTime.now().startOf("day");
        }
        const result = dateFromValue(question, value);
        return result.isValid ? result : undefined;
    };

    maxDate = () => {
        const { max: value, question } = this.props;
        if (isNullOrEmpty(value)) {
            return undefined;
        }
        if (value === "TodayValue") {
            return DateTime.now().endOf("day");
        }
        const result = dateFromValue(question, value);
        return result.isValid ? result : undefined;
    };

    dateValue = () => {
        const { value } = this.state;
        const { question } = this.props;
        if (value === undefined) {
            return undefined;
        }
        return dateFromValue(question, value);
    };

    shouldShowCalendar = (props: Props) => {
        const { question } = props;
        const questionType = getSettingString(question, "Type");
        return ["Date", "DateTime", "Time"].includes(questionType);
    };

    mobiscrollDisplay(calendarSize?: MbscSize): MbscDisplay {
        const isSmall = calendarSize === "xsmall" || calendarSize === "small";
        return isSmall ? "bottom" : "center";
    }

    mobiscrollControls(props: Props): MbscControl[] {
        const { question } = props;
        const type = getSettingString(question, "Type", "Date");
        if (type === "DateTime") {
            return ["calendar", "time"];
        }
        if (type === "Time") {
            return ["time"];
        }
        return ["calendar"];
    }

    bindDatePicker(props: Props) {
        const { question, language } = props;
        if (this.picker) {
            this.picker.destroy();
            this.picker = null;
        }

        const input = this.inputRef;
        const showCalendar = this.shouldShowCalendar(props);
        if (!showCalendar || !input) {
            return;
        }

        const type = getSettingString(question, "Type", "Date");
        const showButtons = !(type === "Date");

        const onClear = (event, instance) => {
            this.setState((prevState) => ({ ...prevState, value: undefined }));
            if (type === "Date" && !showButtons) {
                instance.hide();
            }
        };

        const onSet = (event, instance) => {
            if (type === "Date") {
                return;
            }
            const value = instance.getVal();
            const format = dateFormat(question);
            const luxonValue = DateTime.fromJSDate(value);
            const text = luxonValue.toFormat(format);
            this.setState((prevState) => ({ ...prevState, value: text }));
        };

        const onSetDate = (event, instance) => {
            if (showButtons || !event.control) {
                return;
            }
            const format = dateFormat(question);
            const luxonValue = DateTime.fromJSDate(event.date);
            const text = luxonValue.toFormat(format);
            this.setState((prevState) => ({ ...prevState, value: text }));
            if (type === "Date" && !showButtons) {
                instance.hide();
            }
        };

        const formatValue = () => this.state.value;

        const responsive = {
            xsmall: {
                display: this.mobiscrollDisplay("xsmall"),
            },
            small: {
                display: this.mobiscrollDisplay("small"),
                months: type === "DateTime" ? 1 : 2,
            },
            medium: {
                display: this.mobiscrollDisplay("medium"),
                months: type === "DateTime" ? 1 : 2,
                touchUi: false,
                tabs: false,
            },
        };

        const dateFormatSetting = getSettingString(question, "DateFormat") || "yyyy-mm-dd";
        const pickerDateFormat = cDateFormatToMobiscroll(dateFormatSetting);

        const timeFormatSetting = getSettingString(question, "TimeFormat") || "hh:mm";
        const pickerTimeFormat = cDateFormatToMobiscroll(timeFormatSetting);

        const buttons = showButtons ? ["cancel", "clear", "now", "set"] : ["clear"];

        const options: MbscCalendarOptions = {
            theme: MOBISCROLL_THEME,
            controls: this.mobiscrollControls(props),
            lang: language,
            min: this.minDate(),
            max: this.maxDate(),
            showLabel: true,
            dateFormat: pickerDateFormat,
            timeWheels: pickerTimeFormat,
            headerText: (value) => (value !== undefined ? value : ""),
            invalid: [],
            layout: "fixed",
            onClear,
            onSet,
            onSetDate,
            formatValue,
            responsive,
            buttons,
            width: 100,
        };

        if (hasSetting(question, "DisabledDates")) {
            const disabledDates = JSON.parse(getSettingString(question, "DisabledDates"));
            const shiftWeekDays = DateTime.local().startOf("week").weekday;
            const localizedWeekDays = [...weekDays];
            if (shiftWeekDays) {
                localizedWeekDays.push(localizedWeekDays.shift());
            }
            options.invalid = [
                ...disabledDates.ranges.map(({ start, end }) => ({
                    start: dateFromValue(question, start, false),
                    end: dateFromValue(question, end, false),
                })),
                ...disabledDates.dates.map((date) => dateFromValue(question, date, false)),
                ...disabledDates.weekdays.map(
                    (wkd) => `w${localizedWeekDays.indexOf(wkd) + shiftWeekDays}`,
                ),
            ];
        }

        this.picker = mobiscroll.calendar(input, options);
        if (this.dateValue()) {
            this.picker.setVal(this.dateValue());
        }
    }

    isDateDisabled(date: Date) {
        const { question } = this.props;

        const isDateType =
            getSettingBoolean(question, "Type", "Date") ||
            getSettingBoolean(question, "Type", "DateTime");
        const disabledDates = JSON.parse(getSettingString(question, "DisabledDates", null));

        if (!isDateType || !disabledDates) {
            return false;
        }

        const isDisabledWeekDay = () =>
            disabledDates.weekdays?.some((entry) => entry === date.getDay());

        const isDisabledDay = () =>
            disabledDates.weekdays?.some(
                (entry) => dateFromValue(question, entry).valueOf() === date.valueOf(),
            );

        const isDisabledRange = () =>
            disabledDates.ranges?.some(
                ({ start, end }) =>
                    date >= dateFromValue(question, start).toJSDate() &&
                    date <= dateFromValue(question, end).toJSDate(),
            );

        const isDisabled = isDisabledWeekDay() || isDisabledDay() || isDisabledRange();
        return isDisabled;
    }

    onYearChanged = (event) => {
        const { value } = event.target;
        this.setState((prevState) => ({
            ...prevState,
            year: value,
        }));
    };

    onMonthChanged = ([value]) => {
        const { question } = this.props;
        const [variable] = question.variables;
        this.setMentions(this.props, variable.name, (mentions) => {
            mentions.clear();
            if (!isNullOrEmpty(value)) {
                mentions.set(value);
            }
            return mentions;
        });
        this.setState((prevState) => ({
            ...prevState,
            month: value,
        }));
    };

    onDayChanged = ([value]) => {
        const { question } = this.props;
        const [variable] = question.variables;
        this.setMentions(this.props, variable.name, (mentions) => {
            mentions.clear();
            if (!isNullOrEmpty(value)) {
                mentions.set(value);
            }
            return mentions;
        });
        this.setState((prevState) => ({
            ...prevState,
            date: value,
        }));
    };

    onHourChanged = ([value]) => {
        const { question } = this.props;
        const [variable] = question.variables;
        this.setMentions(this.props, variable.name, (mentions) => {
            mentions.clear();
            if (!isNullOrEmpty(value)) {
                mentions.set(value);
            }
            return mentions;
        });
        this.setState((prevState) => ({
            ...prevState,
            hour: value,
        }));
    };

    onMinuteChanged = ([value]) => {
        const { question } = this.props;
        const [variable] = question.variables;
        this.setMentions(this.props, variable.name, (mentions) => {
            mentions.clear();
            if (!isNullOrEmpty(value)) {
                mentions.set(value);
            }
            return mentions;
        });
        this.setState((prevState) => ({
            ...prevState,
            minute: value,
        }));
    };

    onTextChanged = (event) => {
        const { question } = this.props;
        const { value } = event.target;
        const type = getSettingString(question, "Type");
        if (["Day", "Hours", "Minutes", "Year"].includes(type) && /[^\d]/.test(value)) {
            return;
        }
        this.setState((prevState) => ({
            ...prevState,
            value,
        }));
    };

    onTextBlur = () => {
        const { question } = this.props;
        const [variable] = question.variables;
        const type = getSettingString(question, "Type");
        if (["Date", "DateTime", "Time"].includes(type)) {
            this.onDateBlur();
            return;
        }
        let { value } = this.state;
        const typeBounds: { [type: string]: number[] } = {
            Day: daysRange,
            Hours: hoursRange,
            Minutes: minutesRange,
        };
        if (typeBounds[type] != null && !isNullOrEmpty(value)) {
            const [lowerBound, upperBound] = typeBounds[type];
            if (parseInt(value, 10) < lowerBound) {
                value = String(lowerBound);
            } else if (parseInt(value, 10) > upperBound) {
                value = String(upperBound);
            }
            this.setState((prevState) => ({ ...prevState, value }));
        }
        this.setMentions(this.props, variable.name, (mentions) => mentions.set(value));
    };

    onDateBlur = () => {
        this.setMentionToDate(this.dateValue());
    };

    getNormalizedDate(date: DateTime) {
        const minDate = this.minDate();
        if (minDate != null && date.valueOf() < minDate.valueOf()) {
            return minDate;
        }
        const maxDate = this.maxDate();
        if (maxDate != null && date.valueOf() > maxDate.valueOf()) {
            return maxDate;
        }
        return date;
    }

    setMentionToDate(date: DateTime) {
        const { question } = this.props;
        const [variable] = question.variables;
        if (date == null) {
            this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
            return;
        }
        const normalizedDate = this.getNormalizedDate(date);
        if (getSettingBoolean(question, "SetRespondentAppointment")) {
            this.props.dispatch(respondentSetAppointment(normalizedDate.toJSDate()));
        }
        const text = normalizedDate.toFormat(dateFormat(question));
        this.setState((prevState) => ({ ...prevState, value: text }));
        this.setMentions(this.props, variable.name, (mentions) => mentions.set(text));
    }

    monthArray() {
        return times(12, (i) => DateTime.fromFormat(String(i + 1), "M").toFormat("MMMM"));
    }

    dayArray() {
        const [min, max] = daysRange;
        return range(min, max + 1);
    }

    hourArray() {
        const [min, max] = hoursRange;
        return range(min, max + 1);
    }

    minuteArray() {
        const [min, max] = minutesRange;
        const increment = 5;
        return range(min, max, increment);
    }

    renderYear() {
        return (
            <>
                <TextAnswerRenderer
                    {...getRendererProps(this.props)}
                    inputType="text"
                    maxLength={4}
                    onBlur={this.onTextBlur}
                    onChange={this.onTextChanged}
                    value={this.state.value}
                />
                <ControlContainer
                    controlType={ChoiceListControl}
                    {...getRendererProps(this.props)}
                />
            </>
        );
    }

    renderSelect(value: string, options: Option[], onChange: (value: string[]) => void) {
        const { question, theme, language } = this.props;
        const [variable] = question.variables;
        return (
            <>
                <Select
                    id={`${variable.elementId}_INPUT`}
                    value={[value]}
                    size={size(question)}
                    onChange={onChange}
                    options={[
                        {
                            id: `${variable.elementId}_EMPTY`,
                            label: removeHtmlTags(
                                translate(theme, "DropdownEmptyAnswer", language),
                            ),
                            value: "",
                        },
                        ...options,
                    ]}
                />
                <ControlContainer
                    controlType={ChoiceListControl}
                    {...getRendererProps(this.props)}
                />
            </>
        );
    }

    renderMonth() {
        const { month } = this.state;
        const { question } = this.props;
        const [variable] = question.variables;

        const options = this.monthArray().map((value, index) => ({
            id: `${variable.elementId}_C_${index + 1}`,
            value: (index + 1).toString(),
            label: value,
        }));

        return this.renderSelect(String(month), options, this.onMonthChanged);
    }

    renderDay() {
        const { date } = this.state;
        const { question } = this.props;
        const [variable] = question.variables;

        const options = this.dayArray().map((value) => ({
            id: `${variable.elementId}_C_${value}`,
            value: value.toString(),
            label: value.toString(),
        }));

        return this.renderSelect(date, options, this.onDayChanged);
    }

    renderHours() {
        const { hour } = this.state;
        const { question } = this.props;
        const [variable] = question.variables;

        const options = this.hourArray().map((value) => ({
            id: `${variable.elementId}_C_${value}`,
            value: value.toString(),
            label: value.toString(),
        }));

        return this.renderSelect(hour, options, this.onHourChanged);
    }

    renderMinutes() {
        const { minute } = this.state;
        const { question } = this.props;
        const [variable] = question.variables;

        const options = this.minuteArray().map((value) => ({
            id: `${variable.elementId}_C_${value}`,
            value: value.toString(),
            label: value.toString(),
        }));

        return this.renderSelect(minute, options, this.onMinuteChanged);
    }

    renderDateTime() {
        return (
            <>
                <TextAnswerRenderer
                    {...getRendererProps(this.props)}
                    inputRef={(r) => {
                        this.inputRef = r;
                    }}
                    inputDivRef={(r) => {
                        this.inputDivRef = r;
                    }}
                    inputType="text"
                    onBlur={this.onTextBlur}
                    onChange={this.onTextChanged}
                    openDatePicker={this.openDatePicker}
                    value={this.state.value}
                />
                <ControlContainer
                    controlType={ChoiceListControl}
                    {...getRendererProps(this.props)}
                />
            </>
        );
    }

    renderQuestion(question: QuestionModel) {
        if (this.isMonthType(question)) {
            return this.renderMonth();
        }
        if (this.isYearType(question)) {
            return this.renderYear();
        }
        if (this.isDayType(question)) {
            return this.renderDay();
        }
        if (this.isHourType(question)) {
            return this.renderHours();
        }
        if (this.isMinuteType(question)) {
            return this.renderMinutes();
        }
        if (["Date", "DateTime", "Time"].includes(getSettingString(question, "Type"))) {
            return this.renderDateTime();
        }
        const { min, max } = this.props;
        return (
            <>
                <TextAnswerRenderer
                    {...getRendererProps(this.props)}
                    inputType="text"
                    min={Number(min)}
                    max={Number(max)}
                    onBlur={this.onTextBlur}
                    onChange={this.onTextChanged}
                    value={this.state.value}
                />
                <ControlContainer
                    controlType={ChoiceListControl}
                    {...getRendererProps(this.props)}
                />
            </>
        );
    }

    isAnswerComplete(props: Props) {
        if (isNullOrEmpty(this.state.value) && this.hasMentions(this.props)) {
            const mention = find(this.getMentions(this.props));
            const [variable] = this.props.sequencedVariables;
            if (variable.choiceList.some((choice) => choice.value === mention.value)) {
                return true;
            }
        }
        return !isNullOrEmpty(this.state.value) || super.isAnswerComplete(props);
    }

    isLowerBoundInvalid(date: Date, useTime = true) {
        let min = this.minDate();
        if (min) {
            if (!useTime) {
                min = min.startOf("day");
            }
            return DateTime.fromJSDate(date) < min;
        }
        return false;
    }

    isUpperBoundInvalid(date: Date, useTime = true) {
        let max = this.maxDate();
        if (max) {
            if (!useTime) {
                max = max.endOf("day");
            }
            return DateTime.fromJSDate(date) > max;
        }
        return false;
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { value } = this.state;
        const { question, sequencedVariables, useClientValidation } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const [variable] = sequencedVariables;
        const result = super.validate(options);

        let hasSelectedAnswer = variable.choiceList
            .filter((choice) => getSettingBoolean(choice, "Visible"))
            .some((choice) => choice.value === value);

        if (isNullOrEmpty(value) && this.hasMentions(this.props)) {
            hasSelectedAnswer = variable.choiceList.some(
                (choice) => choice.value === find(this.getMentions(this.props)).value,
            );
        }

        if (result.isValid && !isNullOrEmpty(value) && !hasSelectedAnswer) {
            const type = getSettingString(question, "Type", "");
            const minValue = this.minValue();
            const maxValue = this.maxValue();

            if (["Year", "Month", "Day", "Hours", "Minutes"].includes(type)) {
                // Validate non-date values.
                const intValue = parseInt(value, 10);
                const hasMinValue = minValue !== null && minValue !== undefined;
                const hasMaxValue = maxValue !== null && maxValue !== undefined;

                const [minRange, maxRange] = {
                    Year: [0, 9999],
                    Month: [1, 12],
                    Day: daysRange,
                    Hours: hoursRange,
                    Minutes: minutesRange,
                }[type];

                if (intValue < minRange || intValue > maxRange) {
                    return new ValidationBuilder().fail(variable.name, "InvalidDate").build();
                }

                if (hasMinValue && hasMaxValue) {
                    if (intValue < minValue || intValue > maxValue) {
                        return new ValidationBuilder()
                            .fail(variable.name, "OutOfBounds", {
                                min: String(minValue),
                                max: String(maxValue),
                            })
                            .build();
                    }
                } else if (hasMinValue && !hasMaxValue && intValue < minValue) {
                    return new ValidationBuilder()
                        .fail(variable.name, "LowerBoundNotFulfilled", {
                            min: String(minValue),
                        })
                        .build();
                } else if (!hasMinValue && hasMaxValue && intValue > maxValue) {
                    return new ValidationBuilder()
                        .fail(variable.name, "UpperBoundNotFulfilled", {
                            max: String(maxValue),
                        })
                        .build();
                }
            } else {
                // Validate date values.
                const date = this.dateValue();
                const format = dateFormat(question);

                if (type === "Time" && format && !DateTime.fromFormat(value, format).isValid) {
                    return new ValidationBuilder().fail(variable.name, "InvalidDate").build();
                }

                const dateValue = date.toJSDate();

                if (this.isDateDisabled(dateValue)) {
                    return new ValidationBuilder().fail(variable.name, "UnavailableDate").build();
                }

                if (this.isLowerBoundInvalid(dateValue) || this.isUpperBoundInvalid(dateValue)) {
                    return new ValidationBuilder()
                        .fail(variable.name, "OutOfBounds", {
                            min: this.minDate()?.toFormat(format),
                            max: this.maxDate()?.toFormat(format),
                        })
                        .build();
                }
            }
        }

        return result;
    }
}
